import React, { useState } from 'react';
import { Button } from './ui/Button';
import { Check, ArrowDown, Zap, Globe, MessageSquare } from 'lucide-react';
import { motion } from 'framer-motion';
import { FloatingShape } from './FloatingShape';
import { FormSuccessPopup } from './FormSuccessPopup';
import { EtherealShadow } from './ui/EtherealShadow';
import { useIsMobile } from '../hooks/useIsMobile';
export function HeroSection() {
  const isMobile = useIsMobile();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    businessType: '',
    countryCode: '+44',
    phoneNumber: '',
    bestTimeToCall: '',
    serviceInterest: ''
  });
  const [errors, setErrors] = useState({
    email: '',
    phoneNumber: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const countryOptions = [{
    code: '+44',
    country: 'UK',
    flag: '🇬🇧',
    label: 'United Kingdom'
  }, {
    code: '+1',
    country: 'US',
    flag: '🇺🇸',
    label: 'United States'
  }, {
    code: '+1',
    country: 'CA',
    flag: '🇨🇦',
    label: 'Canada'
  }, {
    code: '+61',
    country: 'AU',
    flag: '🇦🇺',
    label: 'Australia'
  }, {
    code: '+33',
    country: 'EU',
    flag: '🇪🇺',
    label: 'Europe'
  }];
  // Email validation
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
  // Phone validation
  const validatePhone = (phone: string): boolean => {
    const digitsOnly = phone.replace(/\D/g, '');
    return digitsOnly.length >= 10;
  };
  const handleEmailBlur = () => {
    if (formData.email && !validateEmail(formData.email)) {
      setErrors(prev => ({
        ...prev,
        email: 'Please enter a valid email address'
      }));
    } else {
      setErrors(prev => ({
        ...prev,
        email: ''
      }));
    }
  };
  const handlePhoneBlur = () => {
    if (formData.phoneNumber && !validatePhone(formData.phoneNumber)) {
      setErrors(prev => ({
        ...prev,
        phoneNumber: 'Please enter a valid phone number (10 digits minimum)'
      }));
    } else {
      setErrors(prev => ({
        ...prev,
        phoneNumber: ''
      }));
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (e.target.name === 'email' && errors.email) {
      setErrors(prev => ({
        ...prev,
        email: ''
      }));
    }
    if (e.target.name === 'phoneNumber' && errors.phoneNumber) {
      setErrors(prev => ({
        ...prev,
        phoneNumber: ''
      }));
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const emailValid = validateEmail(formData.email);
    const phoneValid = validatePhone(formData.phoneNumber);
    if (!emailValid || !phoneValid) {
      setErrors({
        email: !emailValid ? 'Please enter a valid email address' : '',
        phoneNumber: !phoneValid ? 'Please enter a valid phone number (10 digits minimum)' : ''
      });
      return;
    }
    setIsSubmitting(true);
    const fullPhone = `${formData.countryCode} ${formData.phoneNumber}`;
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          access_key: '811422d7-ceb4-4e48-af50-414ea9cd2a55',
          subject: `🔔 New Lead: ${formData.name} - ${formData.serviceInterest}`,
          from_name: 'Strattigize Contact Form',
          name: formData.name,
          email: formData.email,
          phone: fullPhone,
          best_time: formData.bestTimeToCall,
          business_type: formData.businessType,
          service_interest: formData.serviceInterest,
          message: `
New Service Request

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

👤 CONTACT INFORMATION
Name: ${formData.name}
Email: ${formData.email}
Phone: ${fullPhone}

📞 CALL PREFERENCES
Best Time to Call: ${formData.bestTimeToCall}

🏢 BUSINESS DETAILS
Business Type: ${formData.businessType}

🎯 SERVICE INTERESTED IN
${formData.serviceInterest}

📅 SUBMISSION TIME
${new Date().toLocaleString('en-GB', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            timeZoneName: 'short'
          })}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

⚡ ACTION REQUIRED: Please reach out within 24 hours.
          `.trim()
        })
      });
      const result = await response.json();
      if (result.success) {
        setShowSuccessPopup(true);
        setFormData({
          name: '',
          email: '',
          businessType: '',
          countryCode: '+44',
          phoneNumber: '',
          bestTimeToCall: '',
          serviceInterest: ''
        });
        setErrors({
          email: '',
          phoneNumber: ''
        });
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      alert('Something went wrong. Please try again or contact us at strattigize@gmail.com');
    } finally {
      setIsSubmitting(false);
    }
  };
  return <section className="relative min-h-0 md:min-h-screen flex items-start md:items-center justify-center pt-28 md:pt-20 pb-12 lg:pb-0 overflow-hidden bg-gradient-to-b from-[#FFF8F4] via-white to-white" data-id="element-202">
      {/* Background - Desktop: EtherealShadow, Mobile: Simple gradient */}
      {isMobile ? <div className="absolute inset-0 z-0 bg-gradient-to-br from-primary/5 via-primary/3 to-transparent pointer-events-none" data-id="element-203" /> : <div className="absolute inset-0 z-0 opacity-50 pointer-events-none" data-id="element-204">
          <EtherealShadow color="rgba(232, 76, 26, 0.75)" scale={95} speed={82} noiseOpacity={0.6} noiseScale={1.2} sizing="fill" data-id="element-205" />
        </div>}

      {/* Background Elements - Desktop only */}
      {!isMobile && <div className="absolute inset-0 z-[1]" data-id="element-206">
          <FloatingShape type="cube" className="top-20 left-[10%]" delay={0} data-id="element-207" />
          <FloatingShape type="sphere" className="bottom-40 right-[10%]" delay={2} color="bg-gray-200" data-id="element-208" />
          <FloatingShape type="pyramid" className="top-1/3 right-[20%]" delay={1} data-id="element-209" />
          <motion.div animate={{
        scale: [1, 1.15, 1],
        opacity: [1, 0.7, 1]
      }} transition={{
        duration: 8,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-primary/5 via-transparent to-transparent" data-id="element-210" />
        </div>}

      <div className="container mx-auto px-4 relative z-10 grid lg:grid-cols-2 gap-8 lg:gap-12 items-center" data-id="element-211">
        {/* Left Content */}
        <div className="text-center lg:text-left space-y-3 md:space-y-8" data-id="element-212">
          <motion.div initial={{
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.15,
          ease: 'easeOut'
        }} data-id="element-213">
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black leading-tight tracking-tight text-black" data-id="element-214">
              YOUR BUSINESS DESERVES TO MAKE{' '}
              <span className="text-primary relative inline-block" data-id="element-215">
                MORE MONEY
              </span>
            </h1>
          </motion.div>

          <motion.p initial={{
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.3,
          ease: 'easeOut'
        }} className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto lg:mx-0" data-id="element-216">
            We build automated websites, lead systems, and AI tools that work
            24/7 so you can focus on what matters most.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.45,
          ease: 'easeOut'
        }} className="space-y-1.5 md:space-y-3 text-left max-w-lg mx-auto lg:mx-0" data-id="element-217">
            {[{
            plan: 'Starter',
            price: '£97/mo',
            highlights: ['Free Website', 'On-Page SEO', 'SSL & Fast Loading', 'Lead Capture System']
          }, {
            plan: 'Unlimited',
            price: '£297/mo',
            highlights: ['Everything in Starter', 'CRM + Lead Tracking', '24/7 Live AI ChatBot']
          }, {
            plan: 'Pro',
            price: '£497/mo',
            highlights: ['Everything in Unlimited', '24/7 Live AI Receptionist', 'Automatic Appointments Booking']
          }].map((item, i) => <div key={i} className="flex items-start gap-3 rounded-xl px-3 md:px-4 py-2 md:py-3 border bg-white/60 border-gray-200/80" data-id="element-218">
                <div className="mt-0.5 bg-primary rounded-full p-0.5 flex-shrink-0" data-id="element-219">
                  <Check size={12} className="text-white" data-id="element-220" />
                </div>
                <div className="min-w-0" data-id="element-221">
                  <div className="flex items-center gap-2 flex-wrap" data-id="element-222">
                    <span className="text-black font-bold text-sm md:text-base" data-id="element-223">
                      {item.plan}
                    </span>
                    {item.badge}
                  </div>
                  <p className="text-gray-500 text-xs md:text-sm mt-0.5 line-clamp-2" data-id="element-224">
                    {item.highlights.join(' · ')}
                  </p>
                </div>
              </div>)}
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.6,
          ease: 'easeOut'
        }} className="flex flex-row items-center justify-center lg:justify-start gap-6 sm:gap-8 pt-1 md:pt-4" data-id="element-225">
            <div className="flex flex-col items-center lg:items-start" data-id="element-226">
              <span className="text-xl sm:text-2xl font-bold text-black" data-id="element-227">
                100+
              </span>
              <span className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-wider" data-id="element-228">
                Businesses Automated
              </span>
            </div>
            <div className="h-8 w-px bg-gray-300" data-id="element-229"></div>
            <div className="flex flex-col items-center lg:items-start" data-id="element-230">
              <span className="text-xl sm:text-2xl font-bold text-black" data-id="element-231">
                24/7
              </span>
              <span className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-wider" data-id="element-232">
                Systems Running
              </span>
            </div>
            <div className="h-8 w-px bg-gray-300" data-id="element-233"></div>
            <div className="flex flex-col items-center lg:items-start" data-id="element-234">
              <span className="text-xl sm:text-2xl font-bold text-black" data-id="element-235">
                3x
              </span>
              <span className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-wider" data-id="element-236">
                Average ROI
              </span>
            </div>
          </motion.div>
        </div>

        {/* Right Content - Contact Form */}
        <motion.div initial={{
        opacity: 0,
        x: 30,
        rotateY: 5
      }} animate={{
        opacity: 1,
        x: 0,
        rotateY: 0
      }} transition={{
        duration: 1.1,
        delay: 0.75,
        ease: 'easeOut'
      }} className="relative perspective-container hidden lg:flex items-stretch self-stretch" data-id="element-237">
          <div className="relative w-full overflow-hidden rounded-2xl shadow-2xl" data-id="element-238">
            <img src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=1400&q=90" alt="Business owner working naturally at their desk" className="w-full h-full object-cover" style={{
            minHeight: '100%'
          }} data-id="element-239" />
            <div className="absolute inset-0 bg-black/40" data-id="element-240" />
            <div className="absolute inset-0 flex items-end justify-center px-8 pb-16" data-id="element-241">
              <motion.p initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.9,
              delay: 1.2,
              ease: 'easeOut'
            }} className="font-black text-4xl xl:text-5xl text-center leading-tight tracking-tight drop-shadow-[0_2px_12px_rgba(0,0,0,0.6)]" data-id="element-242">
                <span className="text-white" data-id="element-243">SAVE TIME</span>
                <br data-id="element-244" />
                <span className="text-white text-3xl xl:text-4xl" data-id="element-245">+</span>
                <br data-id="element-246" />
                <span className="text-primary" data-id="element-247">MAKE MONEY</span>
              </motion.p>
            </div>
            <div className="absolute -inset-4 bg-primary/10 rounded-3xl blur-2xl -z-10" data-id="element-248" />
          </div>
        </motion.div>
      </div>

      {/* Scroll Indicator - Desktop only */}
      {!isMobile && <motion.div animate={{
      y: [0, 10, 0]
    }} transition={{
      duration: 2.5,
      repeat: Infinity,
      ease: 'easeInOut'
    }} className="hidden lg:block absolute bottom-8 left-1/2 -translate-x-1/2 text-primary cursor-pointer" onClick={() => {
      const problemSection = document.querySelector('.py-24');
      if (problemSection) {
        problemSection.scrollIntoView({
          behavior: 'smooth'
        });
      }
    }} data-id="element-249">
          <ArrowDown size={32} data-id="element-250" />
        </motion.div>}

      <FormSuccessPopup isOpen={showSuccessPopup} onClose={() => setShowSuccessPopup(false)} data-id="element-251" />
    </section>;
}